import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { ApiError } from '@/lib/api';
import type { CourseAdminView } from '@/lib/types';
import {
  getCourseSegmentsApi,
  replaceCourseSegmentsApi,
  type SegmentInput,
  type SegmentIssue,
} from '@/lib/segments-api';
import { Modal } from '@/components/ui/modal';

/**
 * Issue #342 — 课程分段批次配置弹窗。
 *
 * 按题序范围（1-based 闭区间）配置批次，例如 69 题课程配置 1-35 / 36-69。合法性
 * 校验以后端为准（越界 / 重叠 / 空批次 → 400 + 明确文案），前端做即时提示与展示。
 * 覆盖式保存（先删后建）；SOP 完课口径不变（仍需全部题目）。
 */

const PRIMARY_BTN =
  'inline-flex items-center gap-2 rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-60';
const SECONDARY_BTN =
  'inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs text-slate-600 transition hover:bg-slate-50 disabled:opacity-60';
const INPUT =
  'rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100';

interface Row {
  title: string;
  startSeq: string;
  endSeq: string;
}

export function SegmentConfigModal({
  course,
  onClose,
  onDone,
}: {
  course: CourseAdminView;
  onClose: () => void;
  onDone: () => Promise<unknown> | void;
}) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Issue #380：保存后用 PUT 响应的最新 warnings 覆盖（否则一直显示首次 GET 的旧提示）。
  const [savedWarnings, setSavedWarnings] = useState<SegmentIssue[] | null>(null);

  const query = useQuery({
    queryKey: ['admin', 'course-segments', course.id],
    queryFn: () => getCourseSegmentsApi(course.id),
  });

  // 首次加载后把服务端批次灌入可编辑表格（仅一次）。
  const serverRows = useMemo<Row[]>(
    () =>
      (query.data?.segments ?? []).map((s) => ({
        title: s.title,
        startSeq: String(s.startSeq),
        endSeq: String(s.endSeq),
      })),
    [query.data],
  );
  const editRows = rows ?? serverRows;
  const totalQuestions = query.data?.totalQuestions ?? course.questionCount;

  const setRow = (idx: number, patch: Partial<Row>) => {
    setRows(editRows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const addRow = () => {
    // 便捷默认：新批次从上一批次结束题序 + 1 开始。
    const last = editRows[editRows.length - 1];
    const start = last ? Number(last.endSeq) + 1 : 1;
    const safeStart = Number.isFinite(start) && start >= 1 ? start : 1;
    setRows([
      ...editRows,
      { title: `第 ${editRows.length + 1} 批次`, startSeq: String(safeStart), endSeq: String(Math.min(totalQuestions, safeStart)) },
    ]);
  };
  const removeRow = (idx: number) => setRows(editRows.filter((_, i) => i !== idx));

  const mutation = useMutation({
    mutationFn: () => {
      const payload: SegmentInput[] = editRows.map((r) => ({
        title: r.title.trim(),
        startSeq: Number(r.startSeq),
        endSeq: Number(r.endSeq),
      }));
      return replaceCourseSegmentsApi(course.id, payload);
    },
    onSuccess: async (data) => {
      setError(null);
      // 保存后回灌服务端结果（含 valid / warnings）。
      setRows(
        data.segments.map((s) => ({
          title: s.title,
          startSeq: String(s.startSeq),
          endSeq: String(s.endSeq),
        })),
      );
      // 用最新 PUT 响应的 warnings 覆盖旧 GET 提示（缺口等提示不再滞留）。
      setSavedWarnings(data.warnings);
      await onDone();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : '保存失败，请重试'),
  });

  const warnings = savedWarnings ?? query.data?.warnings ?? [];

  return (
    <Modal
      wide
      title={`分段批次配置 · ${course.name}`}
      description={`按题序范围配置批次（如 1-35、36-69）。当前课程有效题量：${totalQuestions} 题。SOP 完课口径不变——仍需答完全部题目才计入一次完成。`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={SECONDARY_BTN} onClick={onClose}>
            关闭
          </button>
          <button
            type="button"
            className={PRIMARY_BTN}
            disabled={mutation.isPending || query.isLoading}
            onClick={() => {
              setError(null);
              mutation.mutate();
            }}
            data-testid="segment-save"
          >
            {mutation.isPending ? '保存中…' : '保存批次'}
          </button>
        </>
      }
    >
      {query.isLoading ? (
        <div className="py-6 text-center text-sm text-slate-500">正在加载批次配置…</div>
      ) : (
        <div className="space-y-3">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
                  <th className="py-2 pr-3">批次名称</th>
                  <th className="py-2 pr-3">起始题序</th>
                  <th className="py-2 pr-3">结束题序</th>
                  <th className="py-2 pr-3 text-right">操作</th>
                </tr>
              </thead>
              <tbody>
                {editRows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-4 text-center text-xs text-slate-400">
                      暂无批次。点击「添加批次」按题序范围切分课程。
                    </td>
                  </tr>
                ) : (
                  editRows.map((r, idx) => (
                    <tr key={idx} className="border-b border-slate-100">
                      <td className="py-2 pr-3">
                        <input
                          className={`${INPUT} w-40`}
                          value={r.title}
                          maxLength={64}
                          onChange={(e) => setRow(idx, { title: e.target.value })}
                          placeholder="如：第一批次"
                        />
                      </td>
                      <td className="py-2 pr-3">
                        <input
                          className={`${INPUT} w-24`}
                          type="number"
                          min={1}
                          value={r.startSeq}
                          onChange={(e) => setRow(idx, { startSeq: e.target.value })}
                        />
                      </td>
                      <td className="py-2 pr-3">
                        <input
                          className={`${INPUT} w-24`}
                          type="number"
                          min={1}
                          value={r.endSeq}
                          onChange={(e) => setRow(idx, { endSeq: e.target.value })}
                        />
                      </td>
                      <td className="py-2 pr-3 text-right">
                        <button
                          type="button"
                          className={`${SECONDARY_BTN} text-rose-600`}
                          onClick={() => removeRow(idx)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          删除
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <button type="button" className={SECONDARY_BTN} onClick={addRow}>
            <Plus className="h-3.5 w-3.5" />
            添加批次
          </button>

          {warnings.length > 0 && !mutation.isPending ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-800">
              <div className="font-medium">提示（不阻断保存）</div>
              <ul className="mt-1 list-disc space-y-0.5 pl-4">
                {warnings.map((w, i) => (
                  <li key={i}>{w.message}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {error ? (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-2.5 py-2 text-xs text-rose-700">
              {error}
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
